<script setup lang="ts">
import type { MarketingFlowExecutionAction } from '../types/marketingFlow'
import type { ChipColor } from '@/components/base/BaseChip.vue'

const { t } = useI18n()

const props = defineProps<{
  action: MarketingFlowExecutionAction
  getStatusLabel: string
  getStatusColor: ChipColor
}>()

const messageContent = computed(() => {
  return props.action.message?.messageContent || props.action.action.message_content
})

const reward = computed(() => {
  return props.action.action.reward?.name || t('pages.marketingFlow.marketingFlowPerformance.action.deletedReward')
})

const date = computed(() => {
  return new Date(props.action.executed_at ?? props.action.scheduled_for).toLocaleDateString()
})

const time = computed(() => {
  return new Date(props.action.executed_at ?? props.action.scheduled_for).toLocaleTimeString()
})

const icon = computed(() => {
  switch (props.action.action.type) {
    case 'SEND_SMS':
      return 'HeroChatBubbleLeftIconOutline'
    case 'SEND_PUSH':
      return 'HeroBellAlertIconOutline'
    case 'SEND_EMAIL':
      return 'HeroEnvelopeIconOutline'
    case 'REWARD':
      return 'HeroGiftIconOutline'
    case 'BONUS_POINTS':
      return 'HeroCircleStackIconOutline'
    case 'BONUS_MULTIPLIER':
      return 'HeroCalculatorIconSolid'
    case 'CALL_EXTERNAL_API':
      return 'HeroGlobeAltIconOutline'
    default:
      return 'HeroChatBubbleLeftIconOutline'
  }
})

const iconColor = computed(() => {
  switch (props.action.action.type) {
    case 'SEND_SMS':
    case 'SEND_PUSH':
    case 'SEND_EMAIL':
      return 'blue'
    case 'REWARD':
    case 'BONUS_POINTS':
    case 'BONUS_MULTIPLIER':
      return 'yellow'
    case 'CALL_EXTERNAL_API':
      return 'green'
    default:
      return 'slate'
  }
})

const dateIsInvalid = computed(() => date.value === '01/01/1970' || !date.value)
</script>

<template>
  <div class="inline-flex text-sm items-center gap-2">
    <BaseChip v-if="getStatusLabel" size="sm" :text="getStatusLabel" :color="getStatusColor" />
    <BaseChip v-if="icon" :color="iconColor" :icon="icon" />

    <div v-if="action.action.type === 'SEND_SMS'" class="flex flex-col gap-1">
      <span v-if="action.status === 'PENDING'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.future.sendSms', { date, time }) }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.sendSms', { date, time }) }}
      </span>
      <span v-else>
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.sendSms', { date: '', time: '' }) }}
      </span>
      <BaseChip :text="messageContent" />
    </div>

    <div v-else-if="action.action.type === 'SEND_PUSH'" class="flex flex-col gap-1">
      <span v-if="action.status === 'PENDING'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.future.sendPush', { date, time }) }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.sendPush', { date, time }) }}
      </span>
      <span v-else>
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.sendPush', { date: '', time: '' }) }}
      </span>
      <BaseChip :text="messageContent" />
    </div>

    <div v-else-if="action.action.type === 'SEND_EMAIL'" class="flex flex-col gap-1">
      <span v-if="action.status === 'PENDING'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.future.sendEmail', { date, time }) }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.sendEmail', { date, time }) }}
      </span>
      <span v-else>
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.sendEmail', { date: '', time: '' }) }}
      </span>
    </div>

    <div v-else-if="action.action.type === 'REWARD'" class="flex flex-col gap-1">
      <span v-if="action.status === 'WAITING_FOR_CLICK'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.waitingForClick.reward', { reward }) }}
      </span>
      <span v-else-if="action.status === 'PENDING'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.future.reward', { date, time, reward }) }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.reward', { date, time, reward }) }}
      </span>
      <span v-else>
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.reward', { date: '', time: '', reward }) }}
      </span>
    </div>

    <div v-else-if="action.action.type === 'BONUS_POINTS'" class="flex flex-col gap-1">
      <span v-if="action.status === 'WAITING_FOR_CLICK'">
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.waitingForClick.bonusPoints', {
            points: action.action.bonus_points
          })
        }}
      </span>
      <span v-else-if="action.status === 'PENDING'">
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.future.bonusPoints', {
            date,
            time,
            points: action.action.bonus_points
          })
        }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.past.bonusPoints', {
            date,
            time,
            points: action.action.bonus_points
          })
        }}
      </span>
      <span v-else>
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.past.bonusPoints', {
            date: '',
            time: '',
            points: action.action.bonus_points
          })
        }}
      </span>
    </div>

    <div v-else-if="action.action.type === 'BONUS_MULTIPLIER'" class="flex flex-col gap-1">
      <span v-if="action.status === 'WAITING_FOR_CLICK'">
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.waitingForClick.bonusMultiplier', {
            bonusMultiplier: action.action.bonus_multiplier
          })
        }}
      </span>
      <span v-else-if="action.status === 'PENDING'">
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.future.bonusMultiplier', {
            date,
            time,
            bonusMultiplier: action.action.bonus_multiplier
          })
        }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.past.bonusMultiplier', {
            date,
            time,
            bonusMultiplier: action.action.bonus_multiplier
          })
        }}
      </span>
      <span v-else>
        {{
          t('pages.marketingFlow.marketingFlowPerformance.action.past.bonusMultiplier', {
            date: '',
            time: '',
            bonusMultiplier: action.action.bonus_multiplier
          })
        }}
      </span>
    </div>

    <div v-else-if="action.action.type === 'CALL_EXTERNAL_API'" class="flex flex-col gap-1">
      <span v-if="action.status === 'WAITING_FOR_CLICK'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.waitingForClick.callExternalApi') }}
      </span>
      <span v-else-if="action.status === 'PENDING'">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.future.callExternalApi', { date, time }) }}
      </span>
      <span v-else-if="!dateIsInvalid">
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.callExternalApi', { date, time }) }}
      </span>
      <span v-else>
        {{ t('pages.marketingFlow.marketingFlowPerformance.action.past.callExternalApi', { date: '', time: '' }) }}
      </span>
      <div class="flex flex-wrap items-center gap-1">
        <BaseChip :text="action.action.external_api?.method ?? 'POST'" />
        <BaseChip
          v-if="action.action.external_api?.url"
          color="gray"
          :text="action.action.external_api?.url"
          class="max-w-full break-all"
        />
      </div>
    </div>
  </div>
</template>
